import React from "react";
import moment from "moment";

import {
  Box,
  CircularProgress,
  Fade,
  IconButton,
  Typography,
} from "@mui/material";

import { TrackedFlight } from "types";
import { granite } from "globals/design-system/colors";
import { RoundChip } from "components/ui";
import { SyncIcon } from "globals/design-system/icons";
import { flightStatusMap } from "./FlightInfoContent/components";
import PrioritizedFlightInfo from "./PrioritizedFlightInfo";
import { getFlightTimePriorityQueue } from "globals/utils/flight";
import NotificationIcon from "globals/design-system/icons/info/NotificationIcon";

type CompactFlightInfoCardProps = {
  isExpanded: boolean;
  transitStopType: "origin" | "destination";
  trackedFlight: TrackedFlight;
  refreshFlightProps?: {
    onClickRefreshFlight?: () => void;
    isRefreshingFlight?: boolean;
  };
  isOutdatedFlight?: boolean;
};

function CompactFlightInfoCard(props: CompactFlightInfoCardProps) {
  const {
    transitStopType,
    trackedFlight,
    refreshFlightProps = {},
    isExpanded,
    isOutdatedFlight,
  } = props;

  const { onClickRefreshFlight, isRefreshingFlight } = refreshFlightProps;
  const { origin, destination, airline, flightNumber, flightStatus } =
    trackedFlight;

  const airlineHeader = `${airline.airlineName} (${airline.iataCode}) ${flightNumber}`;
  const isDestination = transitStopType === "destination";
  const isCancelledFlight = flightStatus === "CANCELLED";
  const flightTimePriorityQueue = getFlightTimePriorityQueue(
    isDestination ? destination : origin
  );
  const { statusColor, allowRefresh } = flightStatusMap(flightStatus);

  // helpers
  const flightDateOrTime = (dateTime, mode: "date" | "time") =>
    moment(dateTime).format(mode === "date" ? "M/D/YY" : "h:mmA");

  return (
    <>
      <Box
        display="flex"
        alignItems="space-between"
        justifyContent="space-between"
        mb={1}
      >
        {isRefreshingFlight ? (
          <Box p={1.5}>
            <CircularProgress size={20} thickness={4} />
          </Box>
        ) : (
          !!onClickRefreshFlight &&
          allowRefresh && (
            <IconButton onClick={onClickRefreshFlight} size="large">
              <SyncIcon color={granite} />
            </IconButton>
          )
        )}
      </Box>
      <Box display="flex" width="100%" justifyContent="space-between">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
          }}
        >
          <Box display="flex" alignItems="center" mb={1} gap={0.8}>
            <NotificationIcon />
            <Typography
              sx={{
                maxWidth: "220px",
                textDecorationLine: isOutdatedFlight
                  ? "line-through"
                  : undefined,
              }}
              variant="subtitle2"
              fontWeight={700}
            >
              {airlineHeader}
            </Typography>
          </Box>
          <RoundChip
            label={flightStatus}
            color="white"
            backgroundColor={statusColor}
            textTransform="uppercase"
          />
        </Box>
        <Fade in={!isExpanded}>
          <Box
            sx={{
              pl: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              justifyContent: "flex-start",
            }}
          >
            <Typography
              variant="overline"
              sx={{
                textDecorationLine: isOutdatedFlight
                  ? "line-through"
                  : undefined,
              }}
            >
              {flightTimePriorityQueue[0].timeLabel}
            </Typography>
            <PrioritizedFlightInfo
              strikethroughAll={isOutdatedFlight || isCancelledFlight}
              transitStopType={transitStopType}
              primaryText={flightDateOrTime(
                flightTimePriorityQueue[0].time,
                "date"
              )}
              secondaryText={
                flightTimePriorityQueue?.[1]
                  ? flightDateOrTime(flightTimePriorityQueue[1].time, "date")
                  : ""
              }
              primaryProps={{ variant: "caption", style: { fontWeight: 500 } }}
              isTime
            />
            <PrioritizedFlightInfo
              transitStopType={transitStopType}
              primaryText={flightDateOrTime(
                flightTimePriorityQueue[0].time,
                "time"
              )}
              secondaryText={
                flightTimePriorityQueue?.[1]
                  ? flightDateOrTime(flightTimePriorityQueue[1].time, "time")
                  : ""
              }
              primaryProps={{ variant: "subtitle2" }}
              isTime
              strikethroughAll={isCancelledFlight || isOutdatedFlight}
            />
          </Box>
        </Fade>
      </Box>
    </>
  );
}

export default CompactFlightInfoCard;
